import type { PersonDto, SessionView } from "@nabla/api";
import { getJson, signedOutBy, type ClientError, type Reach } from "./http.ts";
import { viewOf } from "./request.ts";

export type SessionState =
  | { readonly status: "carregando" }
  | { readonly status: "dentro"; readonly view: SessionView }
  | { readonly status: "fora"; readonly reason: "terminou" | "saiu" }
  | { readonly status: "erro"; readonly error: ClientError };

interface SessionDeps {
  readonly host: Reach;
  readonly touch: (topic: string) => void;
  readonly heard: (serverTime: string) => void;
  readonly signedIn: () => void;
  readonly forget: (again: boolean) => void;
}

const sessionView = viewOf("sessao")();

export function createSession(deps: SessionDeps) {
  let state: SessionState = { status: "carregando" };
  let generation = 0;
  const me = (): PersonDto["id"] | undefined => (state.status === "dentro" ? state.view.person.id : undefined);
  const forget = (again: boolean) => {
    generation += 1;
    deps.forget(again);
  };
  const signedOut = (reason: "terminou" | "saiu") => {
    if (state.status === "fora") return;
    state = { status: "fora", reason };
    forget(false);
    deps.touch("session");
  };
  const signedIn = (view: SessionView) => {
    const before = me();
    state = { status: "dentro", view };
    deps.heard(view.serverTime);
    if (before !== undefined && before !== view.person.id) forget(true);
    deps.signedIn();
    deps.touch("session");
    deps.touch("threads");
  };
  const putPerson = (person: PersonDto): void => {
    if (!person.isMe || state.status !== "dentro") return;
    if (person.disabled) return signedOut("terminou");
    state = { status: "dentro", view: { ...state.view, person } };
    deps.touch("session");
  };
  const failed = (error: ClientError) => {
    state = { status: "erro", error };
    deps.touch("session");
  };
  return {
    members: { me, signedOut, signedIn, putPerson, session: () => state },
    generation: () => generation,
    async load(): Promise<void> {
      const asked = generation;
      const answer = await getJson(deps.host, sessionView.key);
      if (generation !== asked) return;
      if (!answer.ok) {
        if (!signedOutBy(answer.error, () => signedOut("terminou"))) failed(answer.error);
        return;
      }
      const readout = sessionView.read(answer.held);
      if (readout.ok) return signedIn(readout.value);
      failed({ kind: "formato", detail: readout.detail });
    },
  };
}
